import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FOOTER_GUIDE_LINKS,
  FOOTER_INFO_LINKS,
  FOOTER_PRODUCT_LINKS,
  PRZEWODNIKI_PATH,
  SOCIAL_ICON_PATHS,
  SOCIAL_LINKS,
  type SocialIcon,
} from '@bookkeeper/shared/site-chrome';
import { COMPANY } from '@/lib/company-info';
import { Mail } from 'lucide-react';
import { useHydrated } from '@/hooks/useHydrated';
import { useTranslation } from '@/i18n/i18n';
import { SiteChromeLink } from '@/components/SiteChromeLink';

// The columns, their links and the company line are the shared www chrome
// (@bookkeeper/shared/site-chrome); the interpretation hub renders the same lists.

// Recognizable network glyphs (Interface Craft critique: four near-identical
// mascot tiles beat charm but lost affordance). X has no lucide glyph — a bold
// letterform in the same tile reads unambiguously.
const socialGlyph: Record<SocialIcon, React.ReactNode> = {
  x: <span className="text-lg font-bold leading-none">𝕏</span>,
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" aria-hidden="true">
      <path d={SOCIAL_ICON_PATHS.linkedin} />
    </svg>
  ),
  github: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" aria-hidden="true">
      <path d={SOCIAL_ICON_PATHS.github} />
    </svg>
  ),
  email: <Mail className="size-5" aria-hidden="true" />,
};

const linkClass = 'hover:text-primary transition-colors';

export const Footer = () => {
  const { t } = useTranslation();
  // The copyright year is the visitor's clock, read after hydration: a year
  // baked in by the build would go stale and mismatch at New Year (#9059).
  const [mountYear] = useState(() => new Date().getFullYear());
  const year = useHydrated() ? mountYear : null;

  return (
    <footer className="landing-footer bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-zinc-300 relative overflow-hidden dark:border-t dark:border-border">
      {/* Curved top divider */}
      <div className="absolute top-0 left-0 right-0 h-16">
        <svg
          className="absolute top-0 w-full h-16"
          viewBox="0 0 1440 48"
          preserveAspectRatio="none"
        >
          <path d="M0,48 C480,0 960,0 1440,48 L1440,0 L0,0 Z" fill="hsl(var(--background))" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 pt-24 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <h3 className="text-primary text-xl font-semibold mb-4">{COMPANY.tradingName}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6">
              {t('footer.brandDescription')}
            </p>
            <p className="text-sm text-zinc-500 mb-6">
              {COMPANY.name}
              <br />
              {COMPANY.address}
            </p>
            {/* Social ghost icons */}
            <div className="flex gap-3">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.key}
                  href={social.href}
                  target={social.icon !== 'email' ? '_blank' : undefined}
                  rel={social.icon !== 'email' ? 'noopener noreferrer' : undefined}
                  aria-label={social.label}
                  className="size-11 rounded-xl bg-white/10 text-white/80 flex items-center justify-center transition-all hover:bg-white/20 hover:text-white"
                >
                  {socialGlyph[social.icon]}
                </a>
              ))}
            </div>
          </div>

          {/* Product links */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.product')}</h4>
            <ul className="space-y-3 text-sm">
              {FOOTER_PRODUCT_LINKS.map((link) => (
                <li key={link.id}>
                  <SiteChromeLink
                    link={link}
                    className={linkClass}
                    testId={link.id === 'radian' ? 'footer-radian-link' : undefined}
                  />
                </li>
              ))}
              {/* Przewodniki have no index route — every guide is linked
                  directly so crawlers (and readers) reach each of them
                  from any page on the site. Paths are the canonical Polish
                  ones in every locale: the articles are Polish-only. */}
              <li className="pt-2">
                {/* A heading, not a <span>: the sibling footer groups use one,
                    and it gives the nested list a programmatic label so screen
                    readers announce the group rather than a bare sublist. */}
                <h5
                  id="footer-przewodniki-label"
                  className="block text-xs uppercase tracking-wide text-zinc-500 mb-2 font-normal"
                >
                  {t('footer.przewodniki')}
                </h5>
                <ul className="space-y-2" aria-labelledby="footer-przewodniki-label">
                  {FOOTER_GUIDE_LINKS.map((guide) => (
                    <li key={guide.slug}>
                      <Link to={`${PRZEWODNIKI_PATH}/${guide.slug}`} className={linkClass}>
                        {guide.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.info')}</h4>
            <ul className="space-y-3 text-sm">
              {FOOTER_INFO_LINKS.map((link) => (
                <li key={link.id}>
                  <SiteChromeLink link={link} className={linkClass} />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-zinc-700 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p className="text-zinc-400">
            &copy; {year === null ? '' : `${year} `}
            {COMPANY.name}. {t('footer.allRights')}
          </p>
          <div className="flex flex-wrap gap-6 text-zinc-400">
            <span>NIP: {COMPANY.nip}</span>
            <span>&bull;</span>
            <span>{t('footer.madeWith')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
